import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavbarComponent } from './components/navbar/navbar.component';
import { HeaderBarComponent } from './components/headerbar/headerbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { GlobalLoadingComponent } from './core/shared/global-loading/global-loading.component';
import { SessionMonitorService } from './core/services/session-monitor.service';
import { FooterComponent } from './core/shared/footer/footer.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet, 
    NavbarComponent, 
    HeaderBarComponent, 
    ConfirmDialogModule,
    ToastModule,
    GlobalLoadingComponent,
    FooterComponent,
  ],
  templateUrl: './app.html',
  standalone: true,
  styleUrls: ['./app.scss'],
})
export class App implements OnInit {
  private sessionMonitor = inject(SessionMonitorService);
  
  protected readonly title = signal('pal-fe-ngportal');
  isSidebarCollapsed = false;
  
  private router = signal(new Router());
  
  // Track URL using Signals
  private url = toSignal(
    this.router().events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router().url }
  );
  
  // Computed per controllare se la navbar deve essere visibile
  showNavbar = computed(() => {
    const currentUrl = this.url();
    // Nasconde la navbar su determinate pagine per massimizzare lo spazio
    return !currentUrl?.includes('/axa/professionals-registration-progress') &&
           !currentUrl?.includes('/axa/pre-registration') &&
           !currentUrl?.includes('/axa/professionals-mfa') &&
          !currentUrl?.includes('/axa/professionals-privacy-notice') &&
           !currentUrl?.includes('/auth-test') &&
           !currentUrl?.includes('/unauthorized') &&
           !currentUrl?.includes('/axa/dashboard') &&
           !currentUrl?.includes('/axa/professionals-dashboard') &&
           !currentUrl?.includes('/axa/suppliers-dashboard') &&
           !currentUrl?.includes('/axa/review-professional') &&
           !currentUrl?.includes('/axa/alerts') &&
           !currentUrl?.includes('/axa/vrf-questionnaire') &&
           !currentUrl?.includes('-contract-sections/new') &&
           !currentUrl?.includes('-contract-sections/edit') &&
           !currentUrl?.includes('/questionnaires/') &&
           !currentUrl?.includes('/create-questionnaire') &&
           !currentUrl?.includes('/create-file-checklist') &&
           !currentUrl?.includes('/login') &&
           !currentUrl?.includes('/accessibilita') &&
           !currentUrl?.includes('/informativa-privacy') &&
           !currentUrl?.includes('/__build-info');
  });
  
  // Computed per controllare se l'header deve essere visibile
  showHeader = computed(() => {
    const currentUrl = this.url();
    // Nasconde l'header sulle pagine di accesso/gating iniziale
    return !currentUrl?.includes('/auth-test') &&
          !currentUrl?.includes('/axa/professionals-privacy-notice') &&
          !currentUrl?.includes('/__build-info');
  });
  
  constructor(router: Router) {
    this.router.set(router);
  }

  ngOnInit(): void {
    // Start monitoring session/token expiration
    this.sessionMonitor.startMonitoring();
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
  }

  /**
   * WCAG 2.4.1: Skip-link handler.
   * Prevents Angular Router from intercepting the #main-content fragment href
   * (which would navigate the SPA back to the root/Dashboard route) and instead
   * focuses the <main> element directly so keyboard/screen-reader users skip the
   * header and sidebar and land on the page content immediately.
   */
  skipToMain(event: MouseEvent): void {
    event.preventDefault();
    const main = document.getElementById('main-content');
    if (main) {
      main.focus();
      main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
